import api from './api';

// Company interfaces
export interface Company {
  id: string;
  code: string;
  name: string;
  description?: string;
  legalName?: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  website?: string;
  gstNumber?: string;
  panNumber?: string;
  tanNumber?: string;
  cinNumber?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  swiftCode?: string;
  baseCurrency: string;
  language: string;
  timezone: string;
  logoUrl?: string;
  isPrimary: boolean;
  isActive: boolean;
  settings?: Record<string, any>;
  plants?: Plant[];
  createdAt: string;
  updatedAt: string;
}

// Plant interfaces
export interface Plant {
  id: string;
  code: string;
  name: string;
  type: 'manufacturing' | 'warehouse' | 'service' | 'hybrid';
  companyId: string;
  company?: Company;
  description?: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  managerEmail?: string;
  managerPhone?: string;
  gstNumber?: string;
  isActive: boolean;
  workCenters?: WorkCenter[];
  costCenters?: CostCenter[];
  createdAt: string;
  updatedAt: string;
}

// Work Center interfaces
export interface WorkCenter {
  id: string;
  code: string;
  name: string;
  type: 'machine' | 'labor' | 'assembly' | 'quality' | 'packaging' | 'maintenance';
  plantId: string;
  plant?: Plant;
  costCenterId?: string;
  costCenter?: CostCenter;
  description?: string;
  location?: string;
  status: 'active' | 'inactive' | 'maintenance' | 'breakdown';
  capacity: number;
  standardHoursPerDay: number;
  efficiencyPercent: number;
  utilizationPercent: number;
  hourlyRate: number;
  setupCost: number;
  overheadRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Cost Center interfaces
export interface CostCenter {
  id: string;
  code: string;
  name: string;
  type: 'production' | 'service' | 'support' | 'administration' | 'sales' | 'maintenance';
  plantId: string;
  plant?: Plant;
  parent?: CostCenter;
  children?: CostCenter[];
  description?: string;
  managerName?: string;
  managerEmail?: string;
  annualBudget: number;
  monthlyBudget: number;
  isDirectCost: boolean;
  isProfitCenter: boolean;
  glAccountCode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// UoM interfaces
export interface UoM {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  type: 'quantity' | 'weight' | 'length' | 'area' | 'volume' | 'time';
  description?: string;
  isBaseUnit: boolean;
  conversionFactor: number;
  decimalPlaces: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Item Type interfaces
export interface ItemType {
  id: string;
  code: 'RM' | 'SFG' | 'FG' | 'NON-INV' | 'CONS' | 'SPARE' | 'PACK' | 'TRADING';
  name: string;
  description?: string;
  nature: 'manufactured' | 'purchased' | 'both' | 'service';
  isInventoryTracked: boolean;
  isStockValued: boolean;
  isBatchTracked: boolean;
  isSerialTracked: boolean;
  hasExpiryDate: boolean;
  isMrpApplicable: boolean;
  isPhantomItem: boolean;
  costingMethod: string;
  allowNegativeStock: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Item Attribute interfaces
export interface ItemAttribute {
  id: string;
  code: string;
  name: string;
  description?: string;
  dataType: 'text' | 'number' | 'date' | 'boolean' | 'list' | 'decimal';
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  isVariant: boolean;
  listValues?: string[];
  minValue?: string;
  maxValue?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  defaultValue?: string;
  sortOrder: number;
  groupName?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// BOM interfaces
export interface BOMHeader {
  id: string;
  bomNumber: string;
  itemCode: string;
  itemId: string;
  plantId?: string;
  bomType: 'manufacturing' | 'engineering' | 'sales' | 'costing';
  version: number;
  revision: string;
  status: 'draft' | 'approved' | 'active' | 'inactive' | 'obsolete';
  baseQuantity: number;
  baseUoM: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  materialCost: number;
  laborCost: number;
  overheadCost: number;
  totalCost: number;
  scrapPercentage: number;
  yieldPercentage: number;
  lines?: BOMLine[];
  revisions?: BOMRevision[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BOMLine {
  id: string;
  bomHeaderId: string;
  lineNumber: number;
  componentItemCode: string;
  componentItemId: string;
  componentType: 'material' | 'labor' | 'overhead' | 'outside_processing' | 'tool' | 'fixture';
  supplyType: 'push' | 'pull' | 'backflush' | 'phantom';
  quantity: number;
  uom: string;
  scrapPercentage: number;
  yieldPercentage: number;
  unitCost: number;
  totalCost: number;
  isPhantom: boolean;
  isCritical: boolean;
  isOptional: boolean;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BOMRevision {
  id: string;
  bomHeaderId: string;
  revisionNumber: string;
  versionNumber: number;
  revisionType: 'major' | 'minor' | 'engineering' | 'cost' | 'quality' | 'supplier';
  status: 'draft' | 'pending_approval' | 'approved' | 'active' | 'superseded' | 'cancelled';
  changeDescription: string;
  changeReason?: string;
  effectiveDate: string;
  obsoleteDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Query parameters
export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  [key: string]: any;
}

// Overview of every master, for the master data home page
export type MasterKey =
  | 'companies'
  | 'plants'
  | 'workCenters'
  | 'costCenters'
  | 'uoms'
  | 'itemTypes'
  | 'itemAttributes'
  | 'gstRates'
  | 'boms';

export interface MasterCount {
  key: MasterKey;
  total: number;
  active: number;
  // When a record of this kind was last added or changed; null when there are none
  lastUpdated: string | null;
}

export interface MasterDataOverview {
  masters: MasterCount[];
  primaryCompany: { id: string; code: string; name: string } | null;
  attention: {
    companiesWithoutPlants: number;
    plantsWithoutWorkCenters: number;
    workCentersDown: number;
    bomDrafts: number;
  };
  generatedAt: string;
}

class MasterDataService {
  async getOverview(): Promise<MasterDataOverview> {
    const response = await api.get('/master-data/overview');
    return response.data;
  }

  // Company methods
  async getCompanies(params?: QueryParams): Promise<{ data: Company[]; total: number }> {
    const response = await api.get('/master-data/companies', { params });
    return response.data;
  }

  async getCompanyById(id: string): Promise<Company> {
    const response = await api.get(`/master-data/companies/${id}`);
    return response.data;
  }

  async createCompany(data: Partial<Company>): Promise<Company> {
    const response = await api.post('/master-data/companies', data);
    return response.data;
  }

  async updateCompany(id: string, data: Partial<Company>): Promise<Company> {
    const response = await api.patch(`/master-data/companies/${id}`, data);
    return response.data;
  }

  async deleteCompany(id: string): Promise<void> {
    await api.delete(`/master-data/companies/${id}`);
  }

  // Plant methods
  async getPlants(params?: QueryParams): Promise<{ data: Plant[]; total: number }> {
    const response = await api.get('/master-data/plants', { params });
    return response.data;
  }

  async getPlantById(id: string): Promise<Plant> {
    const response = await api.get(`/master-data/plants/${id}`);
    return response.data;
  }

  async createPlant(data: Partial<Plant>): Promise<Plant> {
    const response = await api.post('/master-data/plants', data);
    return response.data;
  }

  async updatePlant(id: string, data: Partial<Plant>): Promise<Plant> {
    const response = await api.patch(`/master-data/plants/${id}`, data);
    return response.data;
  }

  async deletePlant(id: string): Promise<void> {
    await api.delete(`/master-data/plants/${id}`);
  }

  // Work Center methods
  async getWorkCenters(params?: QueryParams): Promise<{ data: WorkCenter[]; total: number }> {
    const response = await api.get('/master-data/work-centers', { params });
    return response.data;
  }

  async getWorkCenterById(id: string): Promise<WorkCenter> {
    const response = await api.get(`/master-data/work-centers/${id}`);
    return response.data;
  }

  async createWorkCenter(data: Partial<WorkCenter>): Promise<WorkCenter> {
    const response = await api.post('/master-data/work-centers', data);
    return response.data;
  }

  async updateWorkCenter(id: string, data: Partial<WorkCenter>): Promise<WorkCenter> {
    const response = await api.patch(`/master-data/work-centers/${id}`, data);
    return response.data;
  }

  async deleteWorkCenter(id: string): Promise<void> {
    await api.delete(`/master-data/work-centers/${id}`);
  }

  // Cost Center methods
  async getCostCenters(params?: QueryParams): Promise<{ data: CostCenter[]; total: number }> {
    const response = await api.get('/master-data/cost-centers', { params });
    return response.data;
  }

  async getCostCenterById(id: string): Promise<CostCenter> {
    const response = await api.get(`/master-data/cost-centers/${id}`);
    return response.data;
  }

  async createCostCenter(data: Partial<CostCenter>): Promise<CostCenter> {
    const response = await api.post('/master-data/cost-centers', data);
    return response.data;
  }

  async updateCostCenter(id: string, data: Partial<CostCenter>): Promise<CostCenter> {
    const response = await api.patch(`/master-data/cost-centers/${id}`, data);
    return response.data;
  }

  async deleteCostCenter(id: string): Promise<void> {
    await api.delete(`/master-data/cost-centers/${id}`);
  }

  // UoM methods
  async getUoMs(params?: QueryParams): Promise<{ data: UoM[]; total: number }> {
    const response = await api.get('/master-data/uoms', { params });
    return response.data;
  }

  async getUoMById(id: string): Promise<UoM> {
    const response = await api.get(`/master-data/uoms/${id}`);
    return response.data;
  }

  async createUoM(data: Partial<UoM>): Promise<UoM> {
    const response = await api.post('/master-data/uoms', data);
    return response.data;
  }

  async updateUoM(id: string, data: Partial<UoM>): Promise<UoM> {
    const response = await api.patch(`/master-data/uoms/${id}`, data);
    return response.data;
  }

  async deleteUoM(id: string): Promise<void> {
    await api.delete(`/master-data/uoms/${id}`);
  }

  // Item Type methods
  async getItemTypes(params?: QueryParams): Promise<{ data: ItemType[]; total: number }> {
    const response = await api.get('/master-data/item-types', { params });
    return response.data;
  }

  async getItemTypeById(id: string): Promise<ItemType> {
    const response = await api.get(`/master-data/item-types/${id}`);
    return response.data;
  }

  async createItemType(data: Partial<ItemType>): Promise<ItemType> {
    const response = await api.post('/master-data/item-types', data);
    return response.data;
  }

  async updateItemType(id: string, data: Partial<ItemType>): Promise<ItemType> {
    const response = await api.patch(`/master-data/item-types/${id}`, data);
    return response.data;
  }

  async deleteItemType(id: string): Promise<void> {
    await api.delete(`/master-data/item-types/${id}`);
  }

  // Item Attribute methods
  async getItemAttributes(params?: QueryParams): Promise<{ data: ItemAttribute[]; total: number }> {
    const response = await api.get('/master-data/item-attributes', { params });
    return response.data;
  }

  async getItemAttributeById(id: string): Promise<ItemAttribute> {
    const response = await api.get(`/master-data/item-attributes/${id}`);
    return response.data;
  }

  async createItemAttribute(data: Partial<ItemAttribute>): Promise<ItemAttribute> {
    const response = await api.post('/master-data/item-attributes', data);
    return response.data;
  }

  async updateItemAttribute(id: string, data: Partial<ItemAttribute>): Promise<ItemAttribute> {
    const response = await api.patch(`/master-data/item-attributes/${id}`, data);
    return response.data;
  }

  async deleteItemAttribute(id: string): Promise<void> {
    await api.delete(`/master-data/item-attributes/${id}`);
  }

  // BOM methods
  async getBOMs(params?: QueryParams): Promise<{ data: BOMHeader[]; total: number }> {
    const response = await api.get('/master-data/bom-headers', { params });
    return response.data;
  }

  async getBOMById(id: string): Promise<BOMHeader> {
    const response = await api.get(`/master-data/bom-headers/${id}`);
    return response.data;
  }

  async createBOM(data: Partial<BOMHeader>): Promise<BOMHeader> {
    const response = await api.post('/master-data/bom-headers', data);
    return response.data;
  }

  async updateBOM(id: string, data: Partial<BOMHeader>): Promise<BOMHeader> {
    const response = await api.patch(`/master-data/bom-headers/${id}`, data);
    return response.data;
  }

  async deleteBOM(id: string): Promise<void> {
    await api.delete(`/master-data/bom-headers/${id}`);
  }

  async approveBOM(id: string): Promise<BOMHeader> {
    const response = await api.post(`/master-data/bom-headers/${id}/approve`);
    return response.data;
  }

  async createBOMRevision(id: string, data: Partial<BOMRevision>): Promise<BOMRevision> {
    const response = await api.post(`/master-data/bom-headers/${id}/revisions`, data);
    return response.data;
  }
}

const masterDataService = new MasterDataService();
export default masterDataService;